<template>
  <Teleport to="body">
    <div
      class="selection-processes-widget__panel selection-processes-widget__panel--floating"
      :style="panelStyle"
      ref="panelRef"
    >
      <header class="selection-processes-widget__panel-head">
        <h3 class="selection-processes-widget__panel-title">
          {{ t("selectionWidget.runningProcesses") }}
        </h3>
        <button
          type="button"
          class="selection-processes-widget__close"
          @click="emit('close')"
        >
          ✕
        </button>
      </header>

      <section
        v-if="active.length > 0"
        class="selection-processes-widget__group"
      >
        <p class="selection-processes-widget__group-title">
          {{ t("selectionWidget.activeCount", { count: active.length }) }}
        </p>
        <ul class="selection-processes-widget__list">
          <li
            v-for="process in active"
            :key="process.id"
            class="selection-processes-widget__item"
          >
            <button
              type="button"
              class="selection-processes-widget__item-btn"
              @click="onOpen(process.runId)"
            >
              <div class="selection-processes-widget__item-head">
                <span class="selection-processes-widget__item-title">{{ process.title }}</span>
                <span class="selection-processes-widget__item-chip">{{ goalLabel(process.goal) }}</span>
              </div>
              <div
                v-if="process.state === 'purchasing'"
                class="selection-processes-widget__item-status"
              >
                {{ t("selectionWidget.purchasingStatus") }}
              </div>
              <div
                v-else-if="process.positionsTotal > 0"
                class="selection-processes-widget__item-progress"
              >
                <div class="selection-processes-widget__progress-track">
                  <div
                    class="selection-processes-widget__progress-fill"
                    :style="{ width: `${Math.round(process.progress * 100)}%` }"
                  ></div>
                </div>
                <span class="selection-processes-widget__progress-meta">
                  {{ process.positionsFinished }} / {{ process.positionsTotal }}
                </span>
              </div>
            </button>
          </li>
        </ul>
      </section>

      <section
        v-if="completed.length > 0"
        class="selection-processes-widget__group"
      >
        <p class="selection-processes-widget__group-title">
          {{ t("selectionWidget.completedCount", { count: completed.length }) }}
        </p>
        <ul class="selection-processes-widget__list">
          <li
            v-for="process in completed"
            :key="process.id"
            class="selection-processes-widget__item selection-processes-widget__item--completed"
          >
            <button
              type="button"
              class="selection-processes-widget__item-btn"
              @click="onOpen(process.runId)"
            >
              <div class="selection-processes-widget__item-head">
                <span class="selection-processes-widget__item-title">{{ process.title }}</span>
                <span class="selection-processes-widget__item-chip">{{ goalLabel(process.goal) }}</span>
              </div>
              <div class="selection-processes-widget__item-completed">{{ t("selectionWidget.completedStatus") }}</div>
            </button>
          </li>
        </ul>
      </section>

      <p
        v-if="active.length === 0 && completed.length === 0"
        class="selection-processes-widget__empty"
      >
        {{ t("selectionWidget.empty") }}
      </p>

      <footer class="selection-processes-widget__panel-foot">
        <button
          type="button"
          class="selection-processes-widget__open-all"
          @click="onOpenList"
        >
          {{ t("selectionWidget.openSelection") }} →
        </button>
      </footer>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { useSelectionStore } from "../../../../store/eplex/eplexSelection";
import type { SelectionGoal } from "../../../../models/eplex/Selection";

interface AnchorRect {
  top: number;
  right: number;
}

const props = defineProps<{
  anchor: AnchorRect;
}>();

const emit = defineEmits<{
  close: [];
  open: [runId: string];
}>();

const { t } = useI18n();
const router = useRouter();
const store = useSelectionStore();

const panelRef = ref<HTMLElement | null>(null);

const active = computed(() => store.activeProcesses);
const completed = computed(() => store.completedProcesses);

const panelStyle = computed(() => ({
  left: `${props.anchor.right + 8}px`,
  top: `${props.anchor.top}px`,
  maxHeight: `calc(100vh - ${props.anchor.top + 16}px)`,
}));

function goalLabel(goal: SelectionGoal): string {
  return t(`selection.goal.${goal}.label`);
}

function onOpen(runId: string) {
  emit("open", runId);
  emit("close");
  router.push({ name: "eplex-selection-detail", params: { selectionId: runId } });
}

function onOpenList() {
  emit("close");
  router.push("/app/eplex/selection");
}

function handleClickOutside(event: MouseEvent) {
  if (panelRef.value && !panelRef.value.contains(event.target as Node)) {
    emit("close");
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") emit("close");
}

onMounted(() => {
  document.addEventListener("mousedown", handleClickOutside);
  document.addEventListener("keydown", handleKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", handleClickOutside);
  document.removeEventListener("keydown", handleKeydown);
});
</script>
